import { useState } from 'react';

import { Mail, Send } from 'lucide-react';

import type { CandidateRead } from '@/api/schema';
import { Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';

import { useJobShares } from '../api';
import { STATE_LABEL } from '../labels';
import { SendJobProfiles } from './SendJobProfiles';
import styles from './JobProfilesPanel.module.css';

export function JobProfilesPanel({
  candidate,
  canSend,
}: {
  candidate: CandidateRead;
  canSend: boolean;
}) {
  const history = useJobShares(candidate.id);
  const [sending, setSending] = useState(false);
  return (
    <Panel
      title="Job profiles"
      icon={<Mail size={17} />}
      aside={
        canSend ? (
          <Button
            size="sm"
            icon={<Send size={14} />}
            onClick={() => {
              setSending(true);
            }}
          >
            Send job profiles
          </Button>
        ) : undefined
      }
    >
      <p className={styles.hint}>
        Share selected vacancies after your meeting. CVs and other documents are handled
        separately.
      </p>
      {history.isPending ? (
        <Spinner label="Loading sent profiles" />
      ) : history.isError ? (
        <ErrorState
          message="The email history did not load."
          onRetry={() => {
            void history.refetch();
          }}
        />
      ) : history.data.length === 0 ? (
        <p className={styles.hint}>No job profiles have been sent yet.</p>
      ) : (
        <ul className={styles.history}>
          {history.data.map((share) => (
            <li key={share.id}>
              <div className={styles.historyHeading}>
                <strong>{STATE_LABEL[share.state]}</strong>
                <time dateTime={share.created_at}>
                  {new Date(share.created_at).toLocaleString()}
                </time>
              </div>
              <p>To {share.recipient}</p>
              {share.cc?.length ? <p>CC {share.cc.join(', ')}</p> : null}
              <p>{share.vacancies.map((job) => job.title).join(' · ')}</p>
              {share.state !== 'sent' ? <p className={styles.hint}>{share.detail}</p> : null}
            </li>
          ))}
        </ul>
      )}
      {sending ? (
        <SendJobProfiles
          candidate={candidate}
          onClose={() => {
            setSending(false);
          }}
        />
      ) : null}
    </Panel>
  );
}
